/**
 * useSounds Composable
 *
 * Comprehensive sound system for the Canasta game.
 * Pure Vue 3 composable with Web Audio API and Web Speech API.
 *
 * Features:
 * - Web Audio API synthesized sounds (card flip, place, shuffle, meld, canasta)
 * - Web Speech API voice announcements with premium voice selection
 * - Three voice types: dealer (professional), announcer (energetic), counter (clear)
 * - Master gain node for volume control
 * - Concurrent sound limiting (max 6 simultaneous)
 * - Separate controls for sound effects and voice
 * - LocalStorage persistence for user preferences
 * - Natural speech rate (1.0) for human-like announcements
 *
 * @example
 * const { playCardFlip, playPilePickup, speak, speakCounting } = useSounds()
 * playCardFlip()
 * speak('Your turn!', 'announcer')
 * speakCounting([5, 10, 3], 'points for Team A')
 */

import { ref, watch, onMounted } from 'vue'
import { numberToWords } from '../utils/numberToWords'

/**
 * Sound settings (reactive and persistent)
 */
const enabled = ref(true)
const voiceEnabled = ref(true)
const volume = ref(0.5)
let audioContext: AudioContext | null = null
let masterGain: GainNode | null = null
let activeSounds = 0
const MAX_CONCURRENT_SOUNDS = 6

/**
 * Voice type preferences for different announcement contexts
 */
type VoiceType = 'dealer' | 'announcer' | 'counter'

/**
 * Voice parameters per type - balanced for natural speech
 * Slightly below 1.0 for clarity without being slow
 */
const VOICE_PARAMS: Record<VoiceType, { rate: number; pitch: number }> = {
  dealer: { rate: 0.92, pitch: 0.95 },     // Slightly slower, authoritative
  announcer: { rate: 0.95, pitch: 1.0 },   // Natural, clear
  counter: { rate: 1.0, pitch: 1.0 },      // Normal counting pace
}

/**
 * Premium voice selection - prioritize most natural-sounding voices
 * Order matters: first match wins
 */
const PREFERRED_VOICES = {
  // Apple enhanced/premium voices (macOS - very natural)
  appleEnhanced: ['Samantha (Enhanced)', 'Alex (Enhanced)', 'Ava (Enhanced)', 'Tom (Enhanced)'],
  // Google voices (Chrome - high quality)
  google: ['Google US English', 'Google UK English Male', 'Google UK English Female'],
  // Microsoft Neural voices (Edge - very natural)
  microsoft: ['Microsoft Guy Online (Natural)', 'Microsoft Aria Online (Natural)', 'Microsoft Jenny Online (Natural)'],
  // Apple standard voices (macOS fallback)
  apple: ['Samantha', 'Alex', 'Tom', 'Ava', 'Karen', 'Daniel'],
  // Generic English voices as last resort
  generic: ['English (US)', 'English United States', 'en-US'],
}

let selectedVoices: Record<VoiceType, SpeechSynthesisVoice | null> = {
  dealer: null,
  announcer: null,
  counter: null,
}

/**
 * Initialize Web Audio API
 */
function initAudio(): void {
  if (audioContext) return

  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    masterGain = audioContext.createGain()
    masterGain.gain.value = volume.value
    masterGain.connect(audioContext.destination)
  } catch (e) {
    console.warn('Web Audio API not supported:', e)
    enabled.value = false
  }
}

/**
 * Resume audio context (needed for autoplay policies)
 */
async function resumeAudio(): Promise<void> {
  if (audioContext && audioContext.state === 'suspended') {
    await audioContext.resume()
  }
}

/**
 * Ensure audio context is available and not closed
 * Re-initializes if the context was closed (defensive programming)
 * @returns true if audio context is ready to use
 */
function ensureAudioReady(): boolean {
  if (!audioContext || audioContext.state === 'closed') {
    // Re-initialize if context was closed
    audioContext = null
    masterGain = null
    initAudio()
  }
  return !!audioContext && audioContext.state !== 'closed'
}

/**
 * Select premium voices for each voice type
 * Prioritizes enhanced/natural voices for cleaner speech
 */
function selectPremiumVoices(): void {
  if (!('speechSynthesis' in window)) return

  const voices = window.speechSynthesis.getVoices()
  if (voices.length === 0) return

  // Helper to find voice by exact name or partial match
  const findVoice = (names: string[]): SpeechSynthesisVoice | null => {
    for (const name of names) {
      // Try exact match first
      let voice = voices.find((v) => v.name === name)
      if (voice) return voice

      // Try partial match (for enhanced voices that may have different naming)
      voice = voices.find((v) => v.name.toLowerCase().includes(name.toLowerCase()))
      if (voice) return voice
    }
    return null
  }

  // Try each voice category in order of quality
  let bestVoice: SpeechSynthesisVoice | null = null

  // 1. Apple enhanced voices (macOS - most natural)
  bestVoice = findVoice(PREFERRED_VOICES.appleEnhanced)

  // 2. Google voices (Chrome - high quality)
  if (!bestVoice) {
    bestVoice = findVoice(PREFERRED_VOICES.google)
  }

  // 3. Microsoft Neural voices (Edge - very natural)
  if (!bestVoice) {
    bestVoice = findVoice(PREFERRED_VOICES.microsoft)
  }

  // 4. Apple standard voices (macOS fallback)
  if (!bestVoice) {
    bestVoice = findVoice(PREFERRED_VOICES.apple)
  }

  // 5. Generic English voices
  if (!bestVoice) {
    bestVoice = findVoice(PREFERRED_VOICES.generic)
  }

  // 6. Last resort: any en-US or first available
  if (!bestVoice) {
    bestVoice = voices.find((v) => v.lang.startsWith('en')) ?? voices[0] ?? null
  }

  // All voice types use the same premium voice for consistency
  selectedVoices.dealer = bestVoice
  selectedVoices.announcer = bestVoice
  selectedVoices.counter = bestVoice

  console.log('Voice system: Selected', bestVoice?.name || 'default', `(${voices.length} available)`)
}

/**
 * Create a gain node connected to master
 */
function createGain(vol: number = 1): GainNode | null {
  if (!audioContext || !masterGain) return null

  const gain = audioContext.createGain()
  gain.gain.value = vol
  gain.connect(masterGain)
  return gain
}

/**
 * Play multiple layered tones for rich sound
 */
interface SoundLayer {
  freq: number
  freqEnd?: number
  type?: OscillatorType
  attack?: number
  decay?: number
  vol?: number
}

function playLayered(layers: SoundLayer[]): void {
  if (!enabled.value) return
  if (!ensureAudioReady()) return  // Re-initialize if needed
  if (activeSounds >= MAX_CONCURRENT_SOUNDS) return

  resumeAudio()
  activeSounds++

  const now = audioContext.currentTime
  let maxDuration = 0

  layers.forEach((layer) => {
    const osc = audioContext!.createOscillator()
    const gain = createGain(0)
    if (!gain) return

    osc.type = layer.type || 'sine'
    osc.frequency.setValueAtTime(layer.freq, now)

    if (layer.freqEnd) {
      const duration = (layer.attack || 0.01) + (layer.decay || 0.1)
      osc.frequency.exponentialRampToValueAtTime(layer.freqEnd, now + duration)
    }

    // ADSR envelope
    const attack = layer.attack || 0.01
    const decay = layer.decay || 0.1
    const vol = layer.vol || 0.2

    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(vol, now + attack)
    gain.gain.exponentialRampToValueAtTime(0.001, now + attack + decay)

    osc.connect(gain)
    osc.start(now)
    osc.stop(now + attack + decay + 0.05)

    maxDuration = Math.max(maxDuration, attack + decay + 0.05)
  })

  setTimeout(() => activeSounds--, maxDuration * 1000)
}

/**
 * useSounds Composable
 */
export function useSounds() {
  // Initialize audio and voice on first use
  onMounted(() => {
    initAudio()

    // Load settings from localStorage
    const storedEnabled = localStorage.getItem('soundEnabled')
    const storedVoiceEnabled = localStorage.getItem('voiceEnabled')
    const storedVolume = localStorage.getItem('soundVolume')

    if (storedEnabled !== null) enabled.value = storedEnabled === 'true'
    if (storedVoiceEnabled !== null) voiceEnabled.value = storedVoiceEnabled === 'true'
    if (storedVolume !== null) volume.value = parseFloat(storedVolume)

    // Initialize premium voice selection
    selectPremiumVoices()

    // Voice list may load asynchronously, retry if needed
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        selectPremiumVoices()
      }
    }
  })

  // Watch volume changes and update master gain
  watch(volume, (newVolume) => {
    if (masterGain) {
      masterGain.gain.value = newVolume
    }
  })

  // NOTE: We intentionally do NOT clean up on unmount.
  // The AudioContext and speechSynthesis are shared resources that should
  // persist for the lifetime of the app. Cleaning them up when any component
  // unmounts would break voice/sound for other components still using them.

  /**
   * Play card flip/draw sound - swoosh with paper texture
   */
  function playCardFlip(): void {
    playLayered([
      { freq: 600, freqEnd: 150, type: 'sine', attack: 0.01, decay: 0.12, vol: 0.25 },
      { freq: 2200, freqEnd: 800, type: 'triangle', attack: 0.02, decay: 0.08, vol: 0.06 },
      { freq: 120, freqEnd: 60, type: 'sine', attack: 0.01, decay: 0.1, vol: 0.1 },
    ])
  }

  /**
   * Play card place sound - satisfying thud
   */
  function playCardPlace(): void {
    playLayered([
      { freq: 100, freqEnd: 50, type: 'sine', attack: 0.005, decay: 0.1, vol: 0.35 },
      { freq: 180, freqEnd: 80, type: 'triangle', attack: 0.01, decay: 0.08, vol: 0.15 },
      { freq: 800, freqEnd: 400, type: 'sine', attack: 0.02, decay: 0.06, vol: 0.08 },
      { freq: 45, freqEnd: 30, type: 'sine', attack: 0.01, decay: 0.12, vol: 0.12 },
    ])
  }

  /**
   * Play card shuffle sound - rapid fluttering
   */
  function playCardShuffle(): void {
    activeSounds++

    // Multiple rapid flicks to simulate shuffle
    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        playLayered([
          {
            freq: 800 + Math.random() * 600,
            freqEnd: 400,
            type: 'triangle',
            attack: 0.005,
            decay: 0.03,
            vol: 0.1,
          },
        ])
      }, i * 20 + Math.random() * 10)
    }

    setTimeout(() => activeSounds--, 200)
  }

  /**
   * Play pile pickup sound - scaled by pile size
   */
  function playPilePickup(pileSize: number = 5): void {
    // Whoosh intensity scales with pile size
    const intensity = Math.min(pileSize / 20, 1) // 0-1 based on pile size
    const baseVol = 0.2 + intensity * 0.3

    playLayered([
      {
        freq: 300 - intensity * 100,
        freqEnd: 100,
        type: 'sine',
        attack: 0.02,
        decay: 0.2 + intensity * 0.2,
        vol: baseVol,
      },
      {
        freq: 1200,
        freqEnd: 400,
        type: 'triangle',
        attack: 0.01,
        decay: 0.15,
        vol: baseVol * 0.4,
      },
    ])

    // Multiple card flicks based on pile size
    const flicks = Math.min(Math.floor(pileSize / 2), 10)
    activeSounds++
    for (let i = 0; i < flicks; i++) {
      setTimeout(() => {
        playLayered([
          {
            freq: 1200 + Math.random() * 400,
            freqEnd: 600,
            type: 'triangle',
            attack: 0.005,
            decay: 0.04,
            vol: 0.08,
          },
        ])
      }, i * 35 + Math.random() * 10)
    }
    setTimeout(() => activeSounds--, 400)
  }

  /**
   * Play meld created sound - pleasant confirmation
   */
  function playMeldCreated(): void {
    playLayered([
      { freq: 523, type: 'triangle', attack: 0.02, decay: 0.2, vol: 0.2 },
      { freq: 659, type: 'sine', attack: 0.02, decay: 0.18, vol: 0.12 },
      { freq: 784, type: 'sine', attack: 0.03, decay: 0.15, vol: 0.08 },
    ])
  }

  /**
   * Play canasta complete - triumphant celebration
   */
  function playCanastaComplete(): void {
    activeSounds++

    // C major arpeggio
    const notes = [
      { freq: 523, delay: 0 },
      { freq: 659, delay: 80 },
      { freq: 784, delay: 160 },
      { freq: 1047, delay: 240 },
    ]

    notes.forEach((note) => {
      setTimeout(() => {
        playLayered([
          { freq: note.freq, type: 'triangle', attack: 0.02, decay: 0.35, vol: 0.25 },
          { freq: note.freq / 2, type: 'sine', attack: 0.02, decay: 0.3, vol: 0.1 },
          { freq: note.freq * 1.5, type: 'sine', attack: 0.03, decay: 0.2, vol: 0.06 },
        ])
      }, note.delay)
    })

    // Final sparkle
    setTimeout(() => {
      playLayered([
        { freq: 2093, type: 'sine', attack: 0.01, decay: 0.4, vol: 0.08 },
        { freq: 2637, type: 'sine', attack: 0.02, decay: 0.35, vol: 0.05 },
      ])
    }, 350)

    setTimeout(() => activeSounds--, 800)
  }

  /**
   * Play go out sound - triumphant finale
   */
  function playGoOut(): void {
    activeSounds++

    // Ascending victory melody
    const notes = [523, 659, 784, 880, 1047]
    notes.forEach((freq, i) => {
      setTimeout(() => {
        playLayered([
          { freq, type: 'triangle', attack: 0.02, decay: 0.3, vol: 0.3 },
          { freq: freq / 2, type: 'sine', attack: 0.02, decay: 0.25, vol: 0.12 },
        ])
      }, i * 100)
    })

    setTimeout(() => activeSounds--, 800)
  }

  /**
   * Play foot pickup sound - special moment
   */
  function playFootPickup(): void {
    activeSounds++

    // Special rising melody
    const notes = [392, 494, 587, 784]
    notes.forEach((freq, i) => {
      setTimeout(() => {
        playLayered([
          { freq, type: 'triangle', attack: 0.02, decay: 0.25, vol: 0.25 },
          { freq: freq * 1.5, type: 'sine', attack: 0.03, decay: 0.2, vol: 0.1 },
        ])
      }, i * 80)
    })

    setTimeout(() => activeSounds--, 500)
  }

  /**
   * Play turn notification - gentle alert
   */
  function playTurnNotification(): void {
    playLayered([
      { freq: 880, type: 'sine', attack: 0.02, decay: 0.15, vol: 0.15 },
      { freq: 1047, type: 'sine', attack: 0.03, decay: 0.12, vol: 0.1 },
    ])
  }

  /**
   * Play win sound - celebration
   */
  function playWin(): void {
    activeSounds++

    // Triumphant fanfare
    const melody = [523, 659, 784, 1047, 1319]
    melody.forEach((freq, i) => {
      setTimeout(() => {
        playLayered([
          { freq, type: 'triangle', attack: 0.02, decay: 0.35, vol: 0.3 },
          { freq: freq / 2, type: 'sine', attack: 0.02, decay: 0.3, vol: 0.15 },
          { freq: freq * 1.5, type: 'sine', attack: 0.03, decay: 0.25, vol: 0.08 },
        ])
      }, i * 120)
    })

    setTimeout(() => activeSounds--, 1000)
  }

  /**
   * Play lose sound - consolation
   */
  function playLose(): void {
    activeSounds++

    // Descending tones
    const notes = [523, 392, 330, 262]
    notes.forEach((freq, i) => {
      setTimeout(() => {
        playLayered([
          { freq, type: 'sine', attack: 0.02, decay: 0.3, vol: 0.2 },
          { freq: freq / 2, type: 'sine', attack: 0.02, decay: 0.25, vol: 0.1 },
        ])
      }, i * 150)
    })

    setTimeout(() => activeSounds--, 800)
  }

  /**
   * Play error sound - alert
   */
  function playError(): void {
    playLayered([
      { freq: 200, freqEnd: 150, type: 'square', attack: 0.01, decay: 0.15, vol: 0.25 },
      { freq: 100, freqEnd: 80, type: 'sine', attack: 0.01, decay: 0.12, vol: 0.15 },
    ])
  }

  /**
   * Play score count sound - cha-ching register
   * Used when scoring items are announced
   */
  function playScoreCount(): void {
    playLayered([
      { freq: 1200, freqEnd: 800, type: 'triangle', attack: 0.01, decay: 0.15, vol: 0.2 },
      { freq: 2400, freqEnd: 1600, type: 'sine', attack: 0.02, decay: 0.1, vol: 0.08 },
    ])
  }

  /**
   * Play final total sound - triumphant 5-note ascending melody
   * Used when announcing final round totals
   */
  function playFinalTotal(): void {
    activeSounds++

    // C-E-G-C-E ascending triumph
    const notes = [523, 659, 784, 1047, 1319]
    notes.forEach((freq, i) => {
      setTimeout(() => {
        playLayered([
          { freq, type: 'triangle', attack: 0.02, decay: 0.35, vol: 0.25 },
          { freq: freq / 2, type: 'sine', attack: 0.02, decay: 0.3, vol: 0.1 },
        ])
      }, i * 80)
    })

    setTimeout(() => activeSounds--, 600)
  }

  /**
   * Play penalty sound - descending warning
   * Used when showing red 3s or hand penalties
   */
  function playPenalty(): void {
    playLayered([
      { freq: 400, freqEnd: 150, type: 'sawtooth', attack: 0.01, decay: 0.3, vol: 0.2 },
      { freq: 200, freqEnd: 100, type: 'sine', attack: 0.02, decay: 0.25, vol: 0.1 },
    ])
  }

  /**
   * Play click sound - subtle UI feedback
   */
  function playClick(): void {
    playLayered([
      { freq: 1200, freqEnd: 800, type: 'sine', attack: 0.005, decay: 0.04, vol: 0.15 },
      { freq: 2400, freqEnd: 1600, type: 'sine', attack: 0.005, decay: 0.03, vol: 0.05 },
    ])
  }

  /**
   * Play pile appear sound - soft pop
   * Used when 100pt piles appear during counting
   */
  function playPileAppear(): void {
    playLayered([
      { freq: 600, freqEnd: 400, type: 'sine', attack: 0.01, decay: 0.08, vol: 0.12 },
      { freq: 1200, freqEnd: 800, type: 'triangle', attack: 0.01, decay: 0.06, vol: 0.05 },
    ])
  }

  /**
   * Play pile highlight sound - counting tick
   * Used when each pile is highlighted during verbal count
   */
  function playPileHighlight(): void {
    playLayered([
      { freq: 800, type: 'triangle', attack: 0.005, decay: 0.05, vol: 0.1 },
      { freq: 1600, type: 'sine', attack: 0.005, decay: 0.04, vol: 0.04 },
    ])
  }

  /**
   * Speak text using Web Speech API with voice type selection
   * Uses slower rates and warmer pitch for natural human-like speech
   *
   * @param text - Text to speak
   * @param voiceType - Voice type (dealer, announcer, counter)
   * @returns Promise that resolves when speech completes
   */
  function speak(
    text: string | number,
    voiceType: VoiceType = 'announcer'
  ): Promise<void> {
    if (!voiceEnabled.value || !enabled.value) return Promise.resolve()
    if (!('speechSynthesis' in window)) return Promise.resolve()

    return new Promise((resolve) => {
      // Cancel any pending speech to prevent queue buildup
      window.speechSynthesis.cancel()

      // Small delay after cancel for cleaner start
      setTimeout(() => {
        // Convert numbers to words for natural speech
        // Large numbers like 4005 become "four thousand and five"
        let speechText: string
        if (typeof text === 'number') {
          speechText = numberToWords(text)
        } else {
          speechText = text
        }

        const utterance = new SpeechSynthesisUtterance(speechText)

        // Use selected premium voice
        const voice = selectedVoices[voiceType]
        if (voice) {
          utterance.voice = voice
        }

        // Apply per-type rate and pitch for natural speech
        const params = VOICE_PARAMS[voiceType]
        utterance.rate = params.rate
        utterance.pitch = params.pitch
        utterance.volume = Math.min(volume.value * 1.2, 1.0) // Slightly boost voice volume

        // Add generous timeout to ensure completion (based on actual speech text length)
        const timeout = setTimeout(() => resolve(), 1500 + speechText.length * 180)

        utterance.onend = () => {
          clearTimeout(timeout)
          resolve()
        }

        utterance.onerror = () => {
          clearTimeout(timeout)
          resolve()
        }

        window.speechSynthesis.speak(utterance)
      }, 50)
    })
  }

  /**
   * Speak counting sequence for scoring announcements
   * Uses commas for natural flow between numbers
   *
   * @param numbers - Array of numbers to count (e.g., [5, 10, 3])
   * @param suffix - Text to append after counting (e.g., "for fifteen hundred")
   * @example speakCounting([5, 10], 'for one thousand') -> "5, 10, for one thousand"
   */
  async function speakCounting(numbers: number[], suffix: string = ''): Promise<void> {
    if (!voiceEnabled.value || !enabled.value) return
    if (!('speechSynthesis' in window)) return

    // Use commas for natural counting rhythm
    const countingText = numbers.join(', ')
    const fullText = suffix ? `${countingText}, ${suffix}` : countingText

    await speak(fullText, 'counter')
  }

  /**
   * Set sound enabled/disabled
   */
  function setEnabled(value: boolean): void {
    enabled.value = value
    localStorage.setItem('soundEnabled', String(value))
  }

  /**
   * Set voice enabled/disabled
   */
  function setVoiceEnabled(value: boolean): void {
    voiceEnabled.value = value
    localStorage.setItem('voiceEnabled', String(value))
  }

  /**
   * Set volume (0-1)
   */
  function setVolume(value: number): void {
    volume.value = Math.max(0, Math.min(1, value))
    localStorage.setItem('soundVolume', String(volume.value))
    if (masterGain) {
      masterGain.gain.value = volume.value
    }
  }

  /**
   * Stop all currently playing sounds
   * NOTE: This is non-destructive - it resets the counter but does NOT
   * cancel speech synthesis or close the AudioContext, as those are shared
   * resources that other components may still be using.
   */
  function stopAll(): void {
    activeSounds = 0
    // Intentionally NOT canceling speechSynthesis or closing audioContext
    // Those are shared resources that should persist for the app's lifetime
  }

  /**
   * Explicitly destroy audio resources
   * Only call this when the entire app is unmounting (rarely needed)
   * WARNING: This will break sound/voice for any components still mounted
   */
  function destroy(): void {
    activeSounds = 0

    // Cancel any pending speech
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
    }

    // Close Web Audio API context
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().then(() => {
        audioContext = null
        masterGain = null
      }).catch(() => {
        audioContext = null
        masterGain = null
      })
    } else {
      audioContext = null
      masterGain = null
    }
  }

  return {
    // Sound effects
    playCardFlip,
    playCardPlace,
    playCardShuffle,
    playPilePickup,
    playMeldCreated,
    playCanastaComplete,
    playGoOut,
    playFootPickup,
    playTurnNotification,
    playWin,
    playLose,
    playError,

    // Round-end scoring sounds
    playScoreCount,
    playFinalTotal,
    playPenalty,
    playClick,
    playPileAppear,
    playPileHighlight,

    // Voice announcements
    speak,
    speakCounting,

    // Controls
    setEnabled,
    setVoiceEnabled,
    setVolume,
    stopAll,
    destroy,  // Explicit cleanup (rarely needed)

    // Reactive state
    enabled,
    voiceEnabled,
    volume,
  }
}
